'use client';

import { Fragment, useState } from 'react';
import { Checkbox } from '@mokoboko/components';

import { acknowledgmentClause, type TermsTagValues } from '@/helpers/terms';
import { TERMS_LINK_CLASS } from '@/lib/prose';
import { mergeClassNames } from '@/lib/utils';
import type { ResolvedTerms } from '@/services/terms';
import { TermsModal } from './terms-modal';

export type TermsAcceptanceState = {
  docs: ResolvedTerms[];
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
  shake: number;
  acknowledgments: string[];
  showCheckbox: boolean;
  tagValues?: TermsTagValues;
};

// TERMS_LINK_CLASS in the colours a row on the page background reads.
const GROUND_TERMS_LINK_CLASS =
  'text-ground-link underline underline-offset-4 hover:text-ground-link-hover';

export const TermsDocLinks = ({
  docs,
  onOpen,
  onGround = false,
}: {
  docs: ResolvedTerms[];
  onOpen: (doc: ResolvedTerms) => void;
  onGround?: boolean;
}) => (
  <>
    {docs.map((doc, index) => (
      <Fragment key={doc.terms_document_id}>
        {index > 0 ? ' and ' : null}
        <button
          type="button"
          className={onGround ? GROUND_TERMS_LINK_CLASS : TERMS_LINK_CLASS}
          onClick={() => onOpen(doc)}
        >
          {doc.title}
        </button>
      </Fragment>
    ))}
  </>
);

export const TermsAcceptance = ({
  terms,
  hasError,
  errorMessage,
  shake,
  consentAction = 'place this order',
  describedBy,
  onGround = false,
}: {
  terms: TermsAcceptanceState;
  hasError?: boolean;
  errorMessage?: string;
  // Overrides the box's own shake, for a form that shakes every field at once.
  shake?: number;
  // Completes "When you …, you agree to" for someone who already accepted.
  consentAction?: string;
  // An error message rendered elsewhere, for a box that shows none of its own.
  describedBy?: string;
  // Straight on the page background rather than in a card or modal. The row
  // around it carries on-ground for the checkbox's focus ring and error.
  onGround?: boolean;
}) => {
  const {
    docs,
    checked,
    onChange,
    error,
    acknowledgments,
    showCheckbox,
    tagValues,
  } = terms;
  const [openDoc, setOpenDoc] = useState<ResolvedTerms | null>(null);

  if (!docs.length) return null;

  const docLinks = (
    <TermsDocLinks docs={docs} onOpen={setOpenDoc} onGround={onGround} />
  );
  const textClass = onGround ? 'text-ground-body' : 'text-gray-700';

  return (
    <>
      {showCheckbox ? (
        <Checkbox
          checked={checked}
          onCheckedChange={(value) => onChange(value === true)}
          hasError={hasError ?? !!error}
          errorMessage={errorMessage ?? error}
          shake={shake ?? terms.shake}
          // Only when set: the Checkbox spreads this over the description it
          // derives from its own error message.
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          label={
            <span className={mergeClassNames('font-normal', textClass)}>
              I agree to the {docLinks}
              {acknowledgmentClause(acknowledgments)}
            </span>
          }
        />
      ) : (
        <p className={mergeClassNames('text-sm font-normal', textClass)}>
          When you {consentAction}, you agree to the {docLinks}.
        </p>
      )}
      <TermsModal
        doc={openDoc}
        onClose={() => setOpenDoc(null)}
        tagValues={tagValues}
      />
    </>
  );
};
